import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { ClassroomSummary } from '../../classroom360/classroom-summary/models/classroom-summary.model';
import { ClinicVisitLogCodes, MedicalCodeTreatment, MedicalUsers, StaffHealthVisitLog, StaffMedicalCondition } from '../../health360/staff-health/staff-health.model';
import { CheckForErrorsGuard } from '../../shared-components/guards/check-for-errors/check-for-errors.guard';
import { UnsavedChangesGuard } from '../../shared-components/guards/unsaved-changes/unsaved-changes.guard';
import { Staff, StaffEmergencyContact } from '../../shared-components/staff-search/staff.model';
import { System } from '../../shared-components/System';
import { User } from '../../shared-components/user/user.model';
import { YearService } from '../../shared-components/year/year.service';
import { CityStateZip } from './models/city-state-zip.Model';
import { FacultyStaff, StaffAddress, StaffAditionalAdress, StaffAssociation, StaffDocument, StaffDocumentType, StaffStateReporting } from './models/faculty-staff.model';
import { SchoolYearAssignmentPermissions } from './models/school-year-assignment.model';
import { StaffUserRoles } from './models/staffUserRoles.model';
import { PermissionGroupDescription, PermissionsList } from './permissions-groups/permissions-groups.model';
import { StaffAttendance } from './staff-attendance/staff-attendance.model';
// import { StaffMedicalCondition, StaffHealthVisitLog, ClinicVisitLogCodes, MedicalUsers, MedicalCodeTreatment } from './staff-health/staff-health.model';
import { StaffAssignedSchoolsAndRoles } from './staff-header.model';
import { PhotoIdLayout, StaffRolesLookup, StaffUser } from './staff.model';

@Injectable({
  providedIn: 'root'
})
export class StaffService {

  staffList: Staff[] = [];
  staffAlerts: StaffMedicalCondition[] = [];
  staffEmergencyContacts: StaffEmergencyContact[] = [];
  allSchools: SchoolYearAssignmentPermissions[] = [];
  specificSchoolPermissions: boolean;
  copyStaffMedicalConditionRecord = new StaffMedicalCondition();
  copyStaffHealthVisitLog = new StaffHealthVisitLog();
  recordCopied = false;
  familyId: number;
  updatedUsers: number[] = [];
  unsavedChanges: boolean;
  legacySelectedStaffId: number;
  refreshStaffSearchGrid: boolean;
  permissionsGroupsList: PermissionGroupDescription[];
  filteredGridData: SchoolYearAssignmentPermissions[] = [];

  Roles = [
    { RoleName: 'Teacher', RoleDescription: 'Teacher' },
    { RoleName: 'ServiceProvider', RoleDescription: 'SPED-IEP Related Service Provider' },
    { RoleName: 'SpecialEducationTeacher', RoleDescription: 'SPED-IEP Service Teacher' },
    { RoleName: 'SpecialServicesSupervisor', RoleDescription: 'SPED-Supervisor' },
    { RoleName: 'Substitute', RoleDescription: 'Substitute' },
    { RoleName: 'CaseManager', RoleDescription: 'SPED-Case Manager' },
    { RoleName: 'Advisor', RoleDescription: 'Advisor' },
    { RoleName: 'Counselor', RoleDescription: 'Counselor' },
    { RoleName: 'Nurse', RoleDescription: 'Nurse' },
    { RoleName: 'Disciplinarian', RoleDescription: 'Disciplinarian' },
    { RoleName: 'Administrator', RoleDescription: 'Site Administrator' },
    { RoleName: 'Superintendent', RoleDescription: 'District Administrator' },
    { RoleName: 'Principal', RoleDescription: 'Principal' },
    { RoleName: 'Other', RoleDescription: 'Other Staff' }
  ];

  private _selectedStaff: Staff;
  public get selectedStaff(): Staff {
    return this._selectedStaff;
  }

  public set selectedStaff(value: Staff) {
    this._selectedStaff = value;
    this.observableSelectedStaff.next(value);
  }

  public observableSelectedStaff = new Subject<Staff>();
  observableSelectedStaffStream$ = this.observableSelectedStaff.asObservable();

  public observableStateReportingPermition = new Subject<StaffStateReporting>();
  observableStateReportingPermitionStream$ = this.observableStateReportingPermition.asObservable();

  public observableStaffAssignedSchools = new BehaviorSubject<boolean>(false);
  observableStaffAssignedSchoolsStream$ = this.observableStaffAssignedSchools.asObservable();

  constructor(private http: HttpClient,
    private unsavedChangesGuard: UnsavedChangesGuard,
    private checkForErrorsGuard: CheckForErrorsGuard,
    private yearService: YearService) { }

  private _staffCurrentMedicalConditionChecked: boolean;
  public get staffCurrentMedicalConditionChecked(): boolean {
    return this._staffCurrentMedicalConditionChecked;
  }

  public set staffCurrentMedicalConditionChecked(value: boolean) {
    this._staffCurrentMedicalConditionChecked = value;
    this.observableStaffCurrentMedicalConditionChecked.next(value);
  }

  public observableStaffCurrentMedicalConditionChecked = new Subject<boolean>();
  observableStaffCurrentMedicalConditionCheckedStream$ = this.observableStaffCurrentMedicalConditionChecked.asObservable();

  private _medicalConditionShowAlert: boolean;
  public get medicalConditionShowAlert(): boolean {
    return this._medicalConditionShowAlert;
  }

  public set medicalConditionShowAlert(value: boolean) {
    this._medicalConditionShowAlert = value;
    this.observableMedicalConditionShowAlert.next(value);
  }

  public observableMedicalConditionShowAlert = new Subject<boolean>();
  observableMedicalConditionShowAlertStream$ = this.observableMedicalConditionShowAlert.asObservable();

  updateStaff(staffList: Staff[], selectedStaff: Staff) {
    this.staffList = staffList;
    this.selectedStaff = this.staffList.find(x => x.StaffId == selectedStaff.StaffId);
    if (staffList.length > 0) {
      this.updateSelectedStaff(selectedStaff);
    }
    // this.selectedStaff = this.staffList.find(x => x.StaffId == selectedStaff.StaffId);
  }

  setUnmask(phone: string) {
    phone = phone.replace('(', '');
    phone = phone.replace(')', '');
    phone = phone.replace('____', '');
    phone = phone.replace('___', '');
    phone = phone.replace('__', '');
    phone = phone.replace('-', '');
    phone = phone.replace('_', '');
    phone = phone.replace(' ', '');
    return phone.trim();
  }

  getStaffPicture(staffId: number, yearId: number): Observable<Uint8Array> {
    const queryString = '?staffId=' + staffId + '&siteYearId=' + yearId;
    return this.http.get<Uint8Array>(System.apiURL.Management + 'GetStaffPicture' + queryString);
  }

  getStaffHeaderDetail(staffId: number): Observable<Staff> {
    const queryString = '?staffId=' + staffId;
    return this.http.get<Staff>(System.apiURL.Management + 'GetStaffHeaderDetail' + queryString);
  }

  getStaffMedicalAlerts(staffId: number, yearId: number, siteId: number): Observable<StaffMedicalCondition[]> {
    const queryString = '?staffId=' + staffId + '&yearId=' + yearId + '&siteId=' + siteId;
    return this.http.get<StaffMedicalCondition[]>(System.apiURL.Management + 'GetStaffMedicalAlerts' + queryString);
  }

  getStaffAssignedSchools(staffId: number, yearId: number): Observable<StaffAssignedSchoolsAndRoles[]> {
    const queryString = '?staffId=' + staffId + '&yearId=' + yearId;
    return this.http.get<StaffAssignedSchoolsAndRoles[]>(System.apiURL.Management + 'GetStaffAssignedSchools' + queryString);
  }

  getMedicalConditionReadByStaffId(staffId: number, siteId: number, yearId: number, currentMedicalCondition: boolean): Observable<StaffMedicalCondition[]> {
    const showCurrentMedicalCondition = currentMedicalCondition == undefined ? false : currentMedicalCondition;
    const queryString = '?staffId=' + staffId + '&siteId=' + siteId + '&yearId=' + yearId + '&showCurrentMedicalCondition=' + showCurrentMedicalCondition;
    return this.http.get<StaffMedicalCondition[]>(System.apiURL.Management + 'GetMedicalConditionReadByStaffId' + queryString);
  }

  SaveStaffMedicalCondition(staffId: number, siteYearId: number, data: StaffMedicalCondition): Observable<void> {
    const queryString = '?staffId=' + staffId
      + '&siteYearId=' + siteYearId;
    return this.http.post<void>(System.apiURL.Management + 'SaveStaffMedicalCondition' + queryString, data);
  }

  getStaffHealthVisitLogReadByStaffId(staffId: number, siteId: number, yearId: number): Observable<StaffHealthVisitLog[]> {
    const queryString = '?staffId=' + staffId + '&siteId=' + siteId + '&yearId=' + yearId;
    return this.http.get<StaffHealthVisitLog[]>(System.apiURL.Management + 'GetStaffHealthVisitLog' + queryString);
  }

  getStaffHealthVisitLogReadById(visitId: number, siteId: number, yearId: number): Observable<StaffHealthVisitLog> {
    const queryString = '?visitId=' + visitId + '&siteId=' + siteId + '&yearId=' + yearId;
    return this.http.get<StaffHealthVisitLog>(System.apiURL.Management + 'GetStaffHealthVisitLogReadById' + queryString);
  }

  getStaffDocuments(staffId: number, yearId: number): Observable<StaffDocument[]> {
    const queryString = '?staffId=' + staffId + '&yearId=' + yearId;
    return this.http.get<StaffDocument[]>(System.apiURL.Management + 'GetStaffDocuments' + queryString);
  }

  getStaffDocumentType(): Observable<StaffDocumentType[]> {
    return this.http.get<StaffDocumentType[]>(System.apiURL.Management + 'GetStaffDocumentType');
  }

  saveStaffDocument(document: StaffDocument): Observable<StaffDocument[]> {
    return this.http.post<StaffDocument[]>(System.apiURL.Management + 'SaveStaffDocument', document);
  }

  deleteStaffDocument(documentId: number): Observable<boolean> {
    const queryString = '?documentId=' + documentId;
    return this.http.get<boolean>(System.apiURL.Management + 'DeleteStaffDocument' + queryString);
  }

  getClinicLogVisitCodes(): Observable<ClinicVisitLogCodes[]> {
    return this.http.get<ClinicVisitLogCodes[]>(System.apiURL.Management + 'GetClinicLogVisitCodes');
  }

  updateSelectedStaff(staff: Staff) {
    const canContinue = this.unsavedChangesGuard.checkForDeactivation() && this.checkForErrorsGuard.checkForDeactivation();
    if (canContinue instanceof Observable) {
      canContinue.subscribe(clickedOk => {
        if (clickedOk) {
          if (staff != null) {
            this.selectedStaff = staff;
          } else {
            this.observableSelectedStaff.next(null);
          }
        }
      });
    } else if (canContinue) {
      if (staff != null) {
        this.selectedStaff = staff;
      } else {
        this.selectedStaff = null;
        this.observableSelectedStaff.next(null);
      }
    }
  }

  getStaffClassesByStaffId(staffId: number, yearId: number, siteId: number): Observable<ClassroomSummary[]> {
    const queryString = '?staffId=' + staffId + '&yearId=' + yearId + '&siteId=' + siteId;
    return this.http.get<ClassroomSummary[]>(System.apiURL.Classroom + 'GetStaffClassesByStaffId' + queryString);
  }

  getOnDutyUsers(siteYearId: number): Observable<MedicalUsers[]> {
    const queryString = '?siteYearId=' + siteYearId;
    return this.http.get<MedicalUsers[]>(System.apiURL.Management + 'GetMedicalUserBySiteYearId' + queryString);
  }

  getMedicalCodeTreatmentBySiteId(siteId: number): Observable<MedicalCodeTreatment[]> {
    const queryString = '?siteId=' + siteId;
    return this.http.get<MedicalCodeTreatment[]>(System.apiURL.Management + 'GetMedicalCodeTreatmentBySiteId' + queryString);
  }

  deleteHealthVisitLogById(clinicLogId: number): Observable<boolean> {
    const queryString = '?clinicLogId=' + clinicLogId;
    return this.http.get<boolean>(System.apiURL.Management + 'DeleteHealthVisitLogById' + queryString);
  }

  saveHealthVisitLog(staffId: number, siteYearId: number, data: StaffHealthVisitLog): Observable<StaffHealthVisitLog> {
    const queryString = '?staffId=' + staffId
      + '&siteYearId=' + siteYearId;
    return this.http.post<StaffHealthVisitLog>(System.apiURL.Management + 'SaveHealthVisitLog' + queryString, data);
  }
  saveTreatmentCode(clinicLogId: number, data: number[]) {
    const queryString = '?clinicLogId=' + clinicLogId;
    return this.http.post<boolean>(System.apiURL.Management + 'SaveClinicLogToClinicTreatments' + queryString, data);
  }

  deleteClinicTreatmentByClinicLogId(clinicLogId: number): Observable<boolean> {
    const queryString = '?clinicLogId=' + clinicLogId;
    return this.http.get<boolean>(System.apiURL.Management + 'DeleteClinicLogTreatmentByClinicLogId' + queryString);
  }

  getStaffByStaffId(staffId: number, yearId: number): Observable<FacultyStaff[]> {
    const queryString = '?staffId=' + staffId + '&yearId=' + yearId;
    return this.http.get<FacultyStaff[]>(System.apiURL.Management + 'GetFacultyStaffById' + queryString);
  }

  // --------------------
  getPrimayAddressByFamilyId(familyId: number): Observable<StaffAddress[]> {
    const queryString = '?familyId=' + familyId;
    return this.http.get<StaffAddress[]>(System.apiURL.Management + 'GetPrimayAddressByFamilyId' + queryString);
  }

  getAditionlaAddressesByFamilyId(familyId: number): Observable<StaffAditionalAdress[]> {
    const queryString = '?familyId=' + familyId;
    return this.http.get<StaffAditionalAdress[]>(System.apiURL.Management + 'GetAditionlaAddressesByFamilyId' + queryString);
  }

  saveStaffPrimayAddress(address: StaffAddress): Observable<StaffAddress[]> {
    return this.http.post<StaffAddress[]>(System.apiURL.Management + 'SaveStaffPrimayAddress', address);
  }

  saveStaffAditionalAddress(aditionalAddress: StaffAditionalAdress): Observable<StaffAditionalAdress[]> {
    return this.http.post<StaffAditionalAdress[]>(System.apiURL.Management + 'SaveStaffAditionalAddress', aditionalAddress);
  }

  deleteStaffAddress(familyId: number, addressId: number): Observable<boolean> {
    const queryString = '?lngFamilyId=' + familyId + '&lngAddressId=' + addressId;
    return this.http.get<boolean>(System.apiURL.Management + 'DeleteStaffAddress' + queryString);
  }

  isExistStudentAndStaffStrLunchPin(lunchPin: string, studentId: number, staffId: number): Observable<boolean> {
    const queryString = '?lunchPin=' + lunchPin
      + '&studentId=' + studentId
      + '&staffId=' + staffId;
    return this.http.get<boolean>(System.apiURL.Management + 'IsExistStudentAndStaffStrLunchPin' + queryString);
  }

  isExistStudentAndStaffStrID(strIds: string, studentId: number, staffId: number, specialCase = false): Observable<boolean> {
    const queryString = '?strIds=' + strIds
      + '&studentId=' + studentId
      + '&staffId=' + staffId
      + '&specialCase=' + specialCase;
    return this.http.get<boolean>(System.apiURL.Management + 'IsExistStudentAndStaffStrID' + queryString);
  }

  saveFacultyStaff(staff: FacultyStaff): Observable<FacultyStaff> {
    const queryString = '?yearId=' + this.yearService.defaultYear.Id
      + '&siteId=' + this.yearService.defaultYear.SchoolID;
    return this.http.post<FacultyStaff>(System.apiURL.Management + 'SaveFacultyStaff' + queryString, staff);
  }

  saveFacultyStaffPhoto(staff: FacultyStaff): Observable<FacultyStaff> {
    const queryString = '?yearId=' + this.yearService.defaultYear.Id
      + '&siteId=' + this.yearService.defaultYear.SchoolID;
    return this.http.post<FacultyStaff>(System.apiURL.Management + 'SaveFacultyStaffPhoto' + queryString, staff);
  }

  deleteFacultyStafftById(staffId: number): Observable<boolean> {
    const queryString = '?staffId=' + staffId;
    return this.http.get<boolean>(System.apiURL.Management + 'DeleteFacultyStaffById' + queryString);
  }
  facultyStaffReadByNameDuplicateChk(firstName: string, lastName: string, staffId: number): Observable<FacultyStaff[]> {
    const queryString = '?lastName=' + lastName
      + '&firstName=' + firstName
      + '&staffId=' + staffId;
    return this.http.get<FacultyStaff[]>(System.apiURL.Management + 'FacultyStaffReadByNameDuplicateChk' + queryString);
  }

  isStaffInUse(staffId: number): Observable<StaffAssociation[]> {
    const queryString = '?staffId=' + staffId;
    return this.http.get<StaffAssociation[]>(System.apiURL.Management + 'IsStaffInUse' + queryString);
  }

  // Staff Attendance
  getStaffAttendance(staffId: number): Observable<StaffAttendance[]> {
    const queryString = '?yearId=' + this.yearService.defaultYear.Id +
      '&siteId=' + this.yearService.defaultYear.SchoolID +
      '&staffId=' + staffId;
    return this.http.get<StaffAttendance[]>(System.apiURL.Management + 'GetStaffAttendance' + queryString);
  }

  updateStaffAttendance(staffAtt: StaffAttendance): Observable<boolean> {
    const queryString = '?yearId=' + this.yearService.defaultYear.Id +
      '&siteId=' + this.yearService.defaultYear.SchoolID;
    return this.http.post<boolean>(System.apiURL.Management + 'UpdateStaffAttendance' + queryString, staffAtt);
  }

  deleteStaffAttendance(attendanceId: number): Observable<boolean> {
    const queryString = '?yearId=' + this.yearService.defaultYear.Id +
      '&siteId=' + this.yearService.defaultYear.SchoolID +
      '&attendanceId=' + attendanceId;
    return this.http.get<boolean>(System.apiURL.Management + 'DeleteStaffAttendance' + queryString);
  }

  // Staff User
  getStaffUser(staffId: number): Observable<StaffUser> {
    const queryString = '?staffId=' + staffId;
    return this.http.get<StaffUser>(System.apiURL.Management + 'GetStaffUser' + queryString);
  }

  saveStaffUser(staffUser: StaffUser): Observable<number> {
    staffUser.LastSiteYearId = (staffUser.LastSiteYearId <= 0 ? this.yearService.defaultYear.SiteYearId : staffUser.LastSiteYearId);
    const queryString = '?schoolYearId=' + this.yearService.defaultYear.SiteYearId;
    return this.http.post<number>(System.apiURL.Management + 'SaveStaffUser' + queryString, staffUser);
  }
  getUserByUserId(userId: number, userType: number): Observable<User> {
    const queryString = '?userId=' + userId
      + '&userType=' + userType;
    return this.http.get<User>(System.apiURL.Student + 'GetUserByUserId' + queryString);
  }

  // Staff Emergency Contact
  getStaffEmergencyContacts(staffId: number): Observable<StaffEmergencyContact[]> {
    const queryString = '?yearId=' + this.yearService.defaultYear.Id +
      '&siteId=' + this.yearService.defaultYear.SchoolID +
      '&staffId=' + staffId;
    return this.http.get<StaffEmergencyContact[]>(System.apiURL.Management + 'GetStaffEmergencyContacts' + queryString);
  }

  saveStaffEmergencyContactSequence(staffEmergencyContacts: StaffEmergencyContact[]): Observable<boolean> {
    return this.http.post<boolean>(System.apiURL.Management + 'SaveStaffEmergencyContactSequence', staffEmergencyContacts);
  }

  updateStaffEmergencyContact(emergencyContact: StaffEmergencyContact): Observable<number> {
    const queryString = '?yearId=' + this.yearService.defaultYear.Id +
      '&siteId=' + this.yearService.defaultYear.SchoolID;
    return this.http.post<number>(System.apiURL.Management + 'UpdateStaffEmergencyContact' + queryString, emergencyContact);
  }

  deleteStaffEmergencyContact(familyId: number, peopleId: number): Observable<boolean> {
    const queryString = '?yearId=' + this.yearService.defaultYear.Id +
      '&siteId=' + this.yearService.defaultYear.SchoolID +
      '&familyId=' + familyId + '&peopleId=' + peopleId;
    return this.http.get<boolean>(System.apiURL.Management + 'DeleteStaffEmergencyContact' + queryString);
  }

  // School Year Assignment Permissions
  getStaffSchoolAssignments(staffId: number): Observable<SchoolYearAssignmentPermissions[]> {
    const queryString = '?yearId=' + '0' +
      '&staffId=' + staffId;
    return this.http.get<SchoolYearAssignmentPermissions[]>(System.apiURL.Management + 'GetStaffSchoolAssignments' + queryString);
  }

  saveStaffSchoolAssignments(schoolYearAssignmentPermissions: SchoolYearAssignmentPermissions[], specificPermissions: boolean): Observable<boolean> {
    const queryString = '?specificPermissions=' + specificPermissions;
    return this.http.post<boolean>(System.apiURL.Management + 'SaveStaffSchoolAssignments' + queryString, schoolYearAssignmentPermissions);
  }

  saveStaffReporting(staffStateReporting: StaffStateReporting): Observable<Staff> {
    return this.http.post<Staff>(System.apiURL.Management + 'SaveStaffReporting', staffStateReporting);
  }

  getCMPComponentValidation(): Observable<StaffStateReporting[]> {
    return this.http.get<StaffStateReporting[]>(System.apiURL.Management + 'GetCMPComponentValidation');
  }

  getStaffClassifications(): Observable<StaffStateReporting[]> {
    return this.http.get<StaffStateReporting[]>(System.apiURL.Management + 'GetStaffClassifications');
  }

  getStaffHeigherEducations(): Observable<StaffStateReporting[]> {
    return this.http.get<StaffStateReporting[]>(System.apiURL.Management + 'GetStaffHeigherEducations');
  }

  getPhotoIdLayout(siteYearId: number): Observable<PhotoIdLayout[]> {
    const queryString = '?siteYearId=' + siteYearId;
    return this.http.get<PhotoIdLayout[]>(System.apiURL.Management + 'GetPhotoIdLayout' + queryString);
  }

  // Staff Administration - Permissions
  copyUserPermissions(copyFromStaffId: number, copyToStaffIdList: number[], copyPermissionGroups: boolean, copyRoles: boolean, copyIndividualPermissions: boolean, copySchools: boolean, action: boolean): Observable<boolean> {
    const queryString = '?yearId=' + this.yearService.defaultYear.Id +
      '&copyFromStaffId=' + copyFromStaffId +
      '&copyPermissionGroups=' + copyPermissionGroups +
      '&copyRoles=' + copyRoles +
      '&copyIndividualPermissions=' + copyIndividualPermissions +
      '&copySchools=' + copySchools +
      '&action=' + action;
    return this.http.post<boolean>(System.apiURL.Management + 'CopyUserPermissions' + queryString, copyToStaffIdList);
  }

  getStaffRoles(staffId: number, schoolId: number = this.yearService.defaultYear.SchoolID, yearId: number = this.yearService.defaultYear.Id): Observable<StaffUserRoles[]> {
    const queryString = '?yearId=' + yearId
      + '&siteId=' + schoolId
      + '&staffId=' + staffId;
    return this.http.get<StaffUserRoles[]>(System.apiURL.Management + 'GetStaffRoles' + queryString);
  }
  saveStaffRoles(userRoles: StaffUserRoles, schoolId: number = this.yearService.defaultYear.SchoolID, yearId: number = this.yearService.defaultYear.Id): Observable<StaffUserRoles> {
    const queryString = '?yearId=' + yearId
      + '&siteId=' + schoolId;
    return this.http.post<StaffUserRoles>(System.apiURL.Management + 'SaveStaffRoles' + queryString, userRoles);
  }
  clearPersmissions(staffId: number): Observable<void> {
    const queryString = '?yearId=' + this.yearService.defaultYear.Id
      + '&staffId=' + staffId;
    return this.http.get<void>(System.apiURL.Management + 'ClearStaffAllSchoolsPermissions' + queryString);
  }

  mergeStaffAllSchoolsPermissions(staffId: number): Observable<void> {
    const queryString = '?yearId=' + this.yearService.defaultYear.Id
      + '&staffId=' + staffId;
    return this.http.get<void>(System.apiURL.Management + 'MergeStaffAllSchoolsPermissions' + queryString);
  }

  isPhoneValid(phoneNumber: string): boolean {
    let blnIsValid = true;
    if (phoneNumber != null && phoneNumber != '') {
      const pNumber = this.setUnmask(phoneNumber);
      if (pNumber.trim().length < 10 || pNumber.trim().length > 10) {
        blnIsValid = false;
      }
    }
    return blnIsValid;
  }
  isValidEmailAdress(emailAddress: string): Observable<boolean> {
    const queryString = '?yearId=' + this.yearService.defaultYear.Id +
      '&email=' + emailAddress;
    return this.http.get<boolean>(System.apiURL.Management + 'IsValidEmail' + queryString);
  }

  deleteSpousePeopleById(Id: number): Observable<void> {
    const queryString = '?Id=' + Id;
    return this.http.post<void>(System.apiURL.Management + 'DeleteSpousePeople' + queryString, null);
  }

  saveUserPermissions(staffUserId: number, schoolId: number, permissionsList: PermissionsList[]): Observable<boolean> {
    const queryString = '?staffUserId=' + staffUserId + '&schoolId=' + schoolId;
    return this.http.post<boolean>(System.apiURL.Management + 'SaveUserPermissions' + queryString, permissionsList);
  }

  isStaffHasSchoolSpecificPermissions(staffId: number): Observable<number> {
    const queryString = '?staffId=' + staffId;
    return this.http.get<number>(System.apiURL.Management + 'IsStaffHasSchoolSpecificPermissions' + queryString);
  }

  copyUserPermissionsToAssignedSchools(staffId: number, copyFromSchoolId: number, copyToSchoolIdList: number[], copyPermissionGroups: boolean, copyRoles: boolean, copyIndividualPermissions: boolean, copySchools: boolean, action: boolean): Observable<boolean> {
    const queryString = '?yearId=' + this.yearService.defaultYear.Id +
      '&staffId=' + staffId +
      '&copyFromSchoolId=' + copyFromSchoolId +
      '&copyPermissionGroups=' + copyPermissionGroups +
      '&copyRoles=' + copyRoles +
      '&copyIndividualPermissions=' + copyIndividualPermissions +
      '&copySchools=' + copySchools +
      '&action=' + action;
    return this.http.post<boolean>(System.apiURL.Management + 'CopyUserPermissionsToAssignedSchools' + queryString, copyToSchoolIdList);
  }

  isUserNameAlreadyExists(userName: string, peopleId: number): Observable<boolean> {
    const queryString = '?userName=' + userName +
      '&peopleId=' + peopleId;
    return this.http.get<boolean>(System.apiURL.Management + 'IsUserNameAlreadyExists' + queryString);
  }

  getStaffByRoles(yearId: number, roles: string): Observable<StaffRolesLookup[]> {
    const queryString = '?yearId=' + yearId + '&roles=' + roles;
    return this.http.get<StaffRolesLookup[]>(System.apiURL.Management + 'GetStaffByRoles' + queryString);
  }

  copyRolesToAssignedSchools(staffId: number, siteId: number, yearId: number, action: boolean, copyToSchoolIdList: number[]): Observable<boolean> {
    const queryString = '?staffId=' + staffId +
    '&siteId=' + siteId +
    '&yearId=' + yearId +
    '&action=' + action;
    return this.http.post<boolean>(System.apiURL.Management + 'CopyRolesToAssignedSchools' + queryString, copyToSchoolIdList);
  }

  saveCityStateZip(data: CityStateZip): Observable<string> {
    return this.http.post<string>(System.apiURL.Management + 'SaveCityStateZip', data);
  }
}
